import { db } from "../firebaseClient"
import {
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  collection,
  getDocs,
  query,
  where,
  serverTimestamp,
} from "firebase/firestore"
import { generateLocalId } from "./courseHelpers"
import { gradeQuiz } from "./enrollmentHelpers"
import { awardPoints } from "./learnerPointsHelpers"
import { logActivity } from "./activityHelpers"
import { dispatchWebhook } from "./webhookHelpers"
import { listOrgEnrollments } from "./enrollmentHelpers"

// Assessments are a standalone primitive — not owned by any one course.
// skillTags exists now even though nothing reads it yet: it's the data a
// future Skills/Proof-of-Learning page will aggregate, and that only works
// if it's being written from day one.
export const createAssessment = async ({
  orgId,
  title,
  description,
  questions,
  passingScore,
  pointsReward,
  skillTags,
  accessMode,
  assignedUserIds,
}) => {
  if (!orgId || !title?.trim()) {
    return { assessmentId: null, error: { message: "Title is required" } }
  }
  try {
    const assessmentRef = doc(collection(db, "organizations", orgId, "assessments"))
    await setDoc(assessmentRef, {
      title: title.trim(),
      description: description || "",
      questions: questions || [],
      passingScore: passingScore || 70,
      pointsReward: pointsReward || 0,
      skillTags: skillTags || [],
      accessMode: accessMode || "open", // "open" | "assigned"
      assignedUserIds: assignedUserIds || [],
      status: "draft", // "draft" | "published"
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
    return { assessmentId: assessmentRef.id, error: null }
  } catch (error) {
    return { assessmentId: null, error }
  }
}

export const updateAssessment = async (orgId, assessmentId, updates) => {
  try {
    await updateDoc(doc(db, "organizations", orgId, "assessments", assessmentId), {
      ...updates,
      updatedAt: serverTimestamp(),
    })
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const publishAssessment = async (orgId, assessmentId) => updateAssessment(orgId, assessmentId, { status: "published" })
export const unpublishAssessment = async (orgId, assessmentId) => updateAssessment(orgId, assessmentId, { status: "draft" })

export const deleteAssessment = async (orgId, assessmentId) => {
  try {
    await deleteDoc(doc(db, "organizations", orgId, "assessments", assessmentId))
    return { error: null }
  } catch (error) {
    return { error }
  }
}

export const getAssessment = async (orgId, assessmentId) => {
  try {
    const snap = await getDoc(doc(db, "organizations", orgId, "assessments", assessmentId))
    if (!snap.exists()) return { assessment: null, error: null }
    return { assessment: { assessmentId: snap.id, ...snap.data() }, error: null }
  } catch (error) {
    return { assessment: null, error }
  }
}

export const listAssessments = async (orgId) => {
  try {
    const snap = await getDocs(collection(db, "organizations", orgId, "assessments"))
    return { assessments: snap.docs?.map((d) => ({ assessmentId: d.id, ...d.data() })), error: null }
  } catch (error) {
    return { assessments: [], error }
  }
}

// True if this learner is allowed to take this assessment — open assessments
// admit anyone, assigned ones check the explicit list.
export const canTakeAssessment = (assessment, userId) => {
  if (assessment?.status !== "published") return false
  if (assessment?.accessMode === "open") return true
  return !!assessment?.assignedUserIds?.includes(userId)
}

// Deterministic doc ID (mirrors enrollmentDocId's userId_courseId pattern) —
// makes "has this learner already attempted this" a single getDoc.
export const assessmentAttemptDocId = (userId, assessmentId) => `${userId}_${assessmentId}`

export const getAssessmentAttempt = async (userId, assessmentId) => {
  try {
    const snap = await getDoc(doc(db, "assessmentAttempts", assessmentAttemptDocId(userId, assessmentId)))
    if (!snap.exists()) return { attempt: null, error: null }
    return { attempt: snap.data(), error: null }
  } catch (error) {
    return { attempt: null, error }
  }
}

// One locked attempt per learner per standalone assessment — same policy as
// course module quizzes, no retake path yet (course quizzes' retake is
// gated on "finished the whole course," which has no equivalent concept
// for a standalone assessment).
export const submitAssessmentAttempt = async ({ userId, assessmentId, orgId, assessment, answers }) => {
  if (!userId || !assessmentId || !assessment) {
    return { result: null, error: { message: "Missing required fields" } }
  }
  if (!canTakeAssessment(assessment, userId)) {
    return { result: null, error: { message: "You don't have access to this assessment." } }
  }

  try {
    const attemptRef = doc(db, "assessmentAttempts", assessmentAttemptDocId(userId, assessmentId))
    const existing = await getDoc(attemptRef)
    if (existing.exists()) {
      return { result: null, error: { message: "This assessment has already been attempted." } }
    }

    const { score } = gradeQuiz(assessment?.questions, answers)
    const passed = score >= (assessment?.passingScore || 70)

    const attemptData = {
      userId,
      assessmentId,
      orgId,
      answers,
      score,
      passed,
      skillTags: assessment?.skillTags || [],
      attemptedAt: serverTimestamp(),
    }

    await setDoc(attemptRef, attemptData)

    if (passed && assessment?.pointsReward > 0) {
      awardPoints(userId, assessment.pointsReward).catch(() => {})
    }
    if (passed) {
      logActivity(userId, {
        type: "assessment_completed",
        label: `Passed ${assessment?.title || "an assessment"}`,
        icon: "ClipboardCheck",
        color: "text-success",
      }).catch(() => {})
    }
    dispatchWebhook(orgId, "assessment.completed", { userId, assessmentId, score, passed }).catch(() => {})

    return { result: { score, passed }, error: null }
  } catch (error) {
    return { result: null, error }
  }
}

// Learner-facing: this learner's assessment attempts aggregated by
// skillTags — the real (if partial) signal behind "skill development."
// Only reflects standalone assessments; course module quizzes don't carry
// skillTags yet, so this is intentionally scoped, not a full skills picture.
export const listLearnerSkillBreakdown = async (userId) => {
  if (!userId) return { skills: [], error: null }
  try {
    const attemptsRef = collection(db, "assessmentAttempts")
    const q = query(attemptsRef, where("userId", "==", userId))
    const snap = await getDocs(q)
    const attempts = snap.docs?.map((d) => d.data()) || []

    const bySkill = new Map()
    attempts.forEach((a) => {
      (a?.skillTags || []).forEach((tag) => {
        const existing = bySkill.get(tag) || { tag, totalScore: 0, attempts: 0, passed: 0 }
        existing.totalScore += a?.score || 0
        existing.attempts += 1
        if (a?.passed) existing.passed += 1
        bySkill.set(tag, existing)
      })
    })

    const skills = Array.from(bySkill.values())
      .map((s) => ({
        tag: s.tag,
        avgScore: Math.round(s.totalScore / s.attempts),
        attempts: s.attempts,
      }))
      .sort((a, b) => b.avgScore - a.avgScore)

    return { skills, error: null }
  } catch (error) {
    return { skills: [], error }
  }
}

// All attempts for one specific assessment — used by the management UI's
// per-assessment stats.
export const listAssessmentAttempts = async (assessmentId) => {
  try {
    const attemptsRef = collection(db, "assessmentAttempts")
    const q = query(attemptsRef, where("assessmentId", "==", assessmentId))
    const snap = await getDocs(q)
    return { attempts: snap.docs?.map((d) => d.data()), error: null }
  } catch (error) {
    return { attempts: [], error }
  }
}

// Every standalone-assessment attempt across the whole org — combined with
// course-embedded quiz/capstone attempts (see computeOrgAssessmentPerformance
// below) for one org-wide performance number, matching the same combine
// pattern used elsewhere for "aggregate across everything, filter in memory."
export const listOrgAssessmentAttempts = async (orgId) => {
  try {
    const attemptsRef = collection(db, "assessmentAttempts")
    const q = query(attemptsRef, where("orgId", "==", orgId))
    const snap = await getDocs(q)
    return { attempts: snap.docs?.map((d) => d.data()), error: null }
  } catch (error) {
    return { attempts: [], error }
  }
}

// Learner-facing: assessments this learner can take right now but hasn't
// attempted yet, across every org they're enrolled in. N+1 by nature (one
// listAssessments per org, one getAssessmentAttempt per candidate) — fine
// at pilot scale; revisit if a learner ends up enrolled across many orgs.
export const listPendingAssessmentsForLearner = async (orgIds, userId) => {
  if (!userId || !orgIds?.length) return { assessments: [], error: null }

  try {
    const uniqueOrgIds = [...new Set(orgIds)]
    const perOrg = await Promise.all(
      uniqueOrgIds.map(async (orgId) => {
        const { assessments } = await listAssessments(orgId)
        const candidates = (assessments || []).filter((a) => canTakeAssessment(a, userId))
        const withStatus = await Promise.all(
          candidates.map(async (a) => {
            const { attempt } = await getAssessmentAttempt(userId, a.assessmentId)
            return attempt ? null : { ...a, orgId }
          })
        )
        return withStatus.filter(Boolean)
      })
    )
    return { assessments: perOrg.flat(), error: null }
  } catch (error) {
    return { assessments: [], error }
  }
}

// Org-wide "measure understanding" number the vision doc calls for —
// combines course-embedded module quizzes/capstones (already tracked on
// enrollments) with standalone assessment attempts (new collection above)
// into one figure, rather than two disconnected dashboards.
export const computeOrgAssessmentPerformance = async (orgId) => {
  const [{ enrollments }, { attempts: standaloneAttempts }] = await Promise.all([
    listOrgEnrollments(orgId),
    listOrgAssessmentAttempts(orgId),
  ])

  const courseAttempts = []
  enrollments?.forEach((e) => {
    Object.values(e?.moduleQuizResults || {})?.forEach((r) => {
      if (r?.attemptCount) courseAttempts.push({ score: r?.bestScore || 0, passed: !!r?.passed })
    })
    if (e?.capstoneResult?.attemptCount) {
      courseAttempts.push({ score: e?.capstoneResult?.bestScore || 0, passed: !!e?.capstoneResult?.passed })
    }
  })

  const allAttempts = [
    ...courseAttempts,
    ...(standaloneAttempts?.map((a) => ({ score: a?.score || 0, passed: !!a?.passed })) || []),
  ]

  const totalCompleted = allAttempts?.length || 0
  const avgScore = totalCompleted ? Math.round(allAttempts.reduce((s, a) => s + a?.score, 0) / totalCompleted) : 0
  const passCount = allAttempts?.filter((a) => a?.passed)?.length || 0
  const passRate = totalCompleted ? Math.round((passCount / totalCompleted) * 1000) / 10 : 0

  return {
    totalCompleted,
    avgScore,
    passRate,
    courseEmbeddedCount: courseAttempts?.length || 0,
    standaloneCount: standaloneAttempts?.length || 0,
  }
}
